import { useQuery } from "@tanstack/react-query";
import { listDirectory, displayName, type AdminUser } from "../api/tawa";
import {
  listParties,
  getParty,
  listRelationships,
  listRoles,
} from "../api/crm";
import { listProjects, getProject, listWorkItems } from "../api/pm";

/** Annuaire TAWA — membres du tenant (assignés, vue équipe, admin). */
export function useUsers() {
  return useQuery({
    queryKey: ["tawa", "directory"],
    queryFn: listDirectory,
    select: (d) => d.users,
    staleTime: 5 * 60_000,
  });
}

/** Résout actor_ref (worker_url ou username) → nom affichable. */
export function userLabel(users: AdminUser[] | undefined, ref: string): string {
  const u = users?.find(
    (x) =>
      x.worker_url === ref ||
      x.username === ref ||
      x.id === ref ||
      x.email === ref,
  );
  return u ? displayName(u) : ref;
}

// ── CRM (lecture seule, dégradation propre si module absent) ────────────────

export function useParties(q?: string) {
  return useQuery({
    queryKey: ["crm", "parties", q ?? ""],
    queryFn: () => listParties({ q: q || undefined, limit: 100 }),
    select: (d) => d.items,
    retry: 0,
    staleTime: 60_000,
  });
}

export function useParty(id: string | null) {
  return useQuery({
    queryKey: ["crm", "party", id ?? ""],
    queryFn: () => getParty(id!),
    enabled: !!id,
    retry: 0,
  });
}

export function usePartyLinks(id: string | null) {
  return useQuery({
    queryKey: ["crm", "party-links", id ?? ""],
    queryFn: async () => {
      const [rel, roles] = await Promise.all([
        listRelationships(id!),
        listRoles(id!),
      ]);
      return { relationships: rel.items, roles: roles.items };
    },
    enabled: !!id,
    retry: 0,
  });
}

// ── PM (lecture seule) ───────────────────────────────────────────────────────

export function useProjects() {
  return useQuery({
    queryKey: ["pm", "projects"],
    queryFn: () => listProjects(),
    select: (d) => d.items,
    retry: 0,
    staleTime: 60_000,
  });
}

export function useProject(id: string | null) {
  return useQuery({
    queryKey: ["pm", "project", id ?? ""],
    queryFn: () => getProject(id!),
    enabled: !!id,
    retry: 0,
  });
}

export function useWorkItems(projectId: string | null) {
  return useQuery({
    queryKey: ["pm", "work-items", projectId ?? ""],
    queryFn: () => listWorkItems(projectId!),
    select: (d) => d.items,
    enabled: !!projectId,
    retry: 0,
    staleTime: 60_000,
  });
}
